import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, FileJson, FileSpreadsheet, RotateCcw, Terminal, FileText, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UploadZone } from "@/components/UploadZone";

import { RuleTable } from "@/components/RuleTable";

import { ReorderDialog } from "@/components/ReorderDialog";
import { ReportDialog } from "@/components/ReportDialog";
import { analyzeCsv, getSampleCsv } from "@/lib/api";

const downloadFile = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const buildReportCsv = (result) => {
  const header = [
    "row",
    "rule_id",
    "name",
    "action",
    "source",
    "destination",
    "services",
    "protocol",
    "port",
    "status",
    "conflicts_with",
    "explanation",
  ];
  const escape = (v) => {
    const s = String(v ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [header.join(",")];
  for (const r of result.rules) {
    lines.push(
      [
        r.index + 1,
        r.rule_id,
        r.name,
        r.action,
        r.source,
        r.destination,
        r.services || "",
        r.protocol || "",
        r.port || "",
        r.status,
        r.conflicts_with || "",
        r.explanation,
      ]
        .map(escape)
        .join(",")
    );
  }
  return lines.join("\n");
};

export default function Dashboard({ prefill }) {
  const [result, setResult] = useState(prefill || null);
  const [loading, setLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const allowCount =
  result?.summary?.action_distribution?.ALLOW || 0;

  const denyCount =
  result?.summary?.action_distribution?.DENY || 0;
  const handleFile = async (file) => {

    setLoading(true);
    try {
      const data = await analyzeCsv(file);
      setResult(data);
      toast.success(
        `Analysis complete · ${data.summary.total} rules · ${data.summary.shadowed} shadowed · ${data.summary.redundant} redundant · ${data.summary.overlap} overlap`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to analyze CSV");
    } finally {
      setLoading(false);
    }
  };

  const handleSample = async () => {
    setLoading(true);
    try {
      const csvText = await getSampleCsv();
      const file = new File([csvText], "sample-firewall-rules.csv", { type: "text/csv" });
      const data = await analyzeCsv(file);
      setResult(data);
      toast.success("Sample data loaded and analyzed");
    } catch (e) {
      toast.error("Failed to load sample data");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <div className="relative">
      <div className="grid-bg absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-6">
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-5 border-b border-[#1e293b]">
          <div>
            <div className="flex items-center gap-2 text-blue-400 font-mono text-xs tracking-[0.3em] uppercase mb-3">
              <Terminal className="h-3.5 w-3.5" />
              <span className="cursor-blink">policy_inspector</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-none text-slate-50">
              PolicyIQ
              <br />
              <span className="text-blue-400">Firewall Policy Intelligence</span>
            </h1>
            <p className="mt-4 text-slate-400 text-sm max-w-2xl leading-relaxed">
            Identify <span className="text-red-400 font-mono">shadowed</span>,{" "}
            <span className="text-amber-400 font-mono">redundant</span>, and{" "}
            <span className="text-blue-400 font-mono">overlapping</span> rules,
            visualize policy relationships, and optimize firewall configurations
            with confidence.
          </p>
          </div>

          {result && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="reorder-btn"
                variant="outline"
                onClick={() => setReorderOpen(true)}
                className="rounded-sm border-[#1e293b] bg-[#0f172a] hover:bg-[#1e293b] text-slate-100"
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Cleanup
              </Button>
              <Button
                data-testid="download-pdf-btn"
                variant="outline"
                onClick={() => setReportOpen(true)}
                className="rounded-sm border-[#1e293b] bg-[#0f172a] hover:bg-[#1e293b] text-slate-100"
              >
                <FileText className="h-4 w-4 mr-2" />
                PDF Report
              </Button>
              <Button
                data-testid="download-csv-btn"
                variant="outline"
                onClick={() =>
                  downloadFile(buildReportCsv(result), `shadow-report-${Date.now()}.csv`, "text/csv")
                }
                className="rounded-sm border-[#1e293b] bg-[#0f172a] hover:bg-[#1e293b] text-slate-100"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button
                data-testid="download-json-btn"
                variant="outline"
                onClick={() =>
                  downloadFile(
                    JSON.stringify(result, null, 2),
                    `shadow-report-${Date.now()}.json`,
                    "application/json"
                  )
                }
                className="rounded-sm border-[#1e293b] bg-[#0f172a] hover:bg-[#1e293b] text-slate-100"
              >
                <FileJson className="h-4 w-4 mr-2" />
                JSON
              </Button>
              <Button
                data-testid="reset-btn"
                variant="ghost"
                onClick={reset}
                className="rounded-sm text-slate-400 hover:text-slate-100 hover:bg-[#1e293b]"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                New file
              </Button>
            </div>
          )}
        </header>

        {!result ? (
          <section className="pt-10 animate-fade-up">
            <UploadZone onFile={handleFile} onLoadSample={handleSample} loading={loading} />

            <div className="mt-10 grid md:grid-cols-4 gap-4">
              {[
                { tone: "text-red-400", name: "Shadowed", desc: "Unreachable — a prior rule matches all its traffic with a different action." },
                { tone: "text-amber-400", name: "Redundant", desc: "Covered by an earlier rule with the same action. Safe to remove." },
                { tone: "text-blue-400", name: "Overlap", desc: "Partially intersects an earlier rule of a different action — check for unintended shadowing." },
                { tone: "text-emerald-400", name: "Clean", desc: "Reachable and not covered by any prior policy." },
              ].map((c) => (
                <div key={c.name} className="border border-[#1e293b] rounded-sm p-5 bg-[#0f172a]/60">
                  <div className={`flex items-center gap-2 mb-3 ${c.tone}`}>
                    <ShieldAlert className="h-4 w-4" />
                    <span className="text-[10px] tracking-[0.2em] uppercase font-mono">{c.name}</span>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 border border-[#1e293b] rounded-sm p-5 bg-[#0f172a]/60">
              <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400 mb-3">
                Supported input formats
              </div>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-xs font-mono text-slate-400">
                <div><span className="text-slate-200">CIDR</span> → 172.16.0.0/16</div>
                <div><span className="text-slate-200">Plain IP</span> → 172.16.0.1</div>
                <div><span className="text-slate-200">h_</span> → h_172.16.1.2 (host)</div>
                <div><span className="text-slate-200">n_</span> → n_172.16.0.0_16 (network)</div>
                <div><span className="text-slate-200">r_</span> → r_172.16.1.2-172.16.2.2 (range)</div>
                <div><span className="text-slate-200">f_</span> → f_www.google.com (FQDN)</div>
                <div className="md:col-span-2 pt-2 border-t border-[#1e293b] mt-2">
                  <span className="text-slate-200">Services column</span> → TCP_443 · TCP_UDP_443 · "TCP_80,443,8080" · TCP_80;UDP_53
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="pt-8 space-y-6 animate-fade-up">
            <div className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-4">

              <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400 mb-4">
                Analytics Overview
              </div>

              {/* KPI Row */}
              <div className="grid grid-cols-7 gap-4">

              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {result.summary.total}
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Rules
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400">
                  {allowCount}
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Allow
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-red-400">
                  {denyCount}
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Deny
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-red-400">
                  {result.summary.shadowed}
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Shadowed
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400">
                  {result.summary.redundant}
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Cleanup
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">
                  {result.summary.overlap || 0}
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Conflict
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400">
                  {result.summary.clean}
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Healthy
                </div>
              </div>

            </div>

              

            </div>

            

            <div className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400">
                    Rule Analysis
                  </div>
                  <h2 className="text-xl font-semibold text-slate-100 mt-1">
                    Policy Analysis Workspace
                  </h2>
                </div>
              </div>
              <RuleTable rules={result.rules} />
            </div>

            <div className="pt-4 pb-2 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-slate-600">
              <span>analysis_id: {result.id.slice(0, 8)}</span>
              <span>·</span>
              <span>{new Date(result.created_at).toLocaleString()}</span>
            </div>
          </section>
        )}
      </div>

      {result && (
        <>
          <ReportDialog open={reportOpen} onOpenChange={setReportOpen} analysis={result} />
          <ReorderDialog open={reorderOpen} onOpenChange={setReorderOpen} rules={result.rules} />
        </>
      )}
    </div>
  );
}
